import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle, Shield } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your admin email address");
      return;
    }

    if (!API_URL) {
      console.error('[AdminForgotPassword] REACT_APP_API_URL is not configured');
      toast.error("Service temporarily unavailable. Please try again later.");
      return;
    }
    
    setLoading(true);
    console.log('[AdminForgotPassword] Requesting reset for:', email.substring(0, 3) + '***');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });
      
      const data = await res.json();
      console.log('[AdminForgotPassword] Response:', res.status, data.status);
      
      if (res.ok) {
        setSent(true);
        toast.success("Check your email for the reset link");
      } else {
        toast.error(data.detail || "Something went wrong");
      }
    } catch (error) {
      console.error('[AdminForgotPassword] Fetch error:', error.message);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4" data-testid="admin-forgot-success">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <CardTitle className="text-2xl text-white">Check Your Email</CardTitle>
            <CardDescription className="text-slate-400">
              If an admin account exists for <strong className="text-slate-300">{email}</strong>, you'll receive a password reset link shortly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={() => setSent(false)} className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                Try a different email
              </Button>
              <Link to="/admin/login" className="w-full">
                <Button variant="ghost" className="w-full text-slate-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Admin Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4" data-testid="admin-forgot-page">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <CardTitle className="text-2xl text-white">Admin Password Reset</CardTitle>
          <CardDescription className="text-slate-400">
            Enter your admin email address to receive a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="admin-forgot-email-input"
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="admin-forgot-submit-btn">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/admin/login" className="text-sm text-blue-400 hover:underline inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Admin Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
